import React, { useState, useLayoutEffect, useEffect } from "react";

import Header from "./header";
import LoanToolControls from "./loan_tool_controls";
import LoanToolNavigation from "./loan_tool_navigation";
import UpdateBanner from "./update_banner";
import SaveFailedBanner from "./save_failed_banner";

import "./css/standard_template.scss";
import { useUser } from "../../hooks/user";
import { class_list } from "../../constants/utils";
import { is_wholesale_view_only } from "../../constants/lender_visibility";
import { DocumentContext } from "../../components/loan_tools/loan_tool";

export default function StandardTemplate({
  children,
  active,
  basic,
  className,
  isDisableNavSave,
  showMoreLinks
}) {
  // Read the loan-tool document context (present on loan/form pages, undefined on
  // basic pages). Called first so hook order is stable across the early return.
  const documentCtx = React.useContext(DocumentContext);

  // Back from a form: SAVE any unsaved loan edits first, then go back. Protects
  // every ba-template form (letters, auth, realtor, etc.) from losing work when
  // the LO leaves. Falls back to a plain back when there's no document context.
  async function handleBackWithSave() {
    try {
      const actions = documentCtx && documentCtx[3];
      if (actions && typeof actions.saveDocument === "function") {
        await actions.saveDocument();
      }
    } catch (e) {
      // non-fatal — still allow navigation; values remain on screen
    }
    window.history.back();
  }

  if (!useUser()) {
    return (
      <div className={class_list("standard-template", className && className)}>
        <Header />
        <div>{children}</div>
      </div>
    );
  }

  const [{ isLoggedIn, accountProfile }] = useUser();
  // Wholesale Account Executives get a view-only loan file: they can open and
  // read a broker's loan (incl. lender-specific info) but cannot edit it, and
  // they don't get pricing/Summary. Detected from the management role on profile.
  // VISION (wholesale) loan files are view-only for the whole wholesale chain
  // (AE up through wholesale management), not just AEs.
  const isAccountExecutive = is_wholesale_view_only(accountProfile);
  // Demo/setup edit-unlock: an admin can temporarily bypass the AE view-only
  // lock (to build/edit test loans) by entering the setup code. Stored per
  // browser so normal demo viewing stays locked.
  const editUnlocked =
    typeof window !== "undefined" &&
    window.localStorage.getItem("demo_edit_unlock") === "1";
  const aeViewOnly = isAccountExecutive && !basic && !editUnlocked;

  function toggleEditUnlock() {
    if (editUnlocked) {
      window.localStorage.removeItem("demo_edit_unlock");
      window.location.reload();
      return;
    }
    const code = window.prompt(
      "Enter the demo setup code to enable editing for this account:"
    );
    if (code == null) return;
    if (code.trim().toLowerCase() === "lanisedit") {
      window.localStorage.setItem("demo_edit_unlock", "1");
      window.location.reload();
    } else {
      window.alert("Incorrect setup code.");
    }
  }
  const [bodyHeight, setBodyHeight] = useState(0);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [atTop, setAtTop] = useState(true);

  useLayoutEffect(() => {
    const height = window.document.body.offsetHeight;
    if (bodyHeight !== height) {
      setBodyHeight(height);
      updateAtTop();
      updateScrollToTop();
    }
  });

  useEffect(() => {
    window.addEventListener("resize", updateScrollToTop);

    return () => {
      window.removeEventListener("resize", updateScrollToTop);
    };
  }, [showScrollToTop]);

  useEffect(() => {
    window.addEventListener("scroll", updateAtTop);

    return () => {
      window.removeEventListener("scroll", updateAtTop);
    };
  }, [atTop]);

  function updateScrollToTop() {
    const isShow = window.innerHeight < window.document.body.offsetHeight;
    if (showScrollToTop !== isShow) {
      setShowScrollToTop(isShow);
    }
  }

  function updateAtTop() {
    const isAtTop = document.documentElement.scrollTop === 0;
    if (atTop !== isAtTop) {
      setAtTop(isAtTop);
    }
  }

  function scrollToTop() {
    window.scrollTo(document.documentElement.scrollLeft, 0);
  }

  return (
    <div
      className={class_list(
        "standard-template",
        className && className,
        aeViewOnly && "ae-view-only"
      )}
    >
      <div className="standard-template-content">
        <Header showMoreLinks={showMoreLinks} />
        <UpdateBanner />
        <SaveFailedBanner />
        {!basic && isLoggedIn && <LoanToolControls aeViewOnly={aeViewOnly} />}
        {!basic && isLoggedIn && (
          <LoanToolNavigation
            active={active}
            isDisableNavSave={isDisableNavSave || aeViewOnly}
            aeViewOnly={aeViewOnly}
          />
        )}
        {aeViewOnly && (
          <div className="ae-view-only-banner no-print">
            View only — VISION (Wholesale). You can review this loan and its
            KPIs for your lender&#39;s loans, but cannot edit it.
            <button className="button ae-unlock-button" onClick={toggleEditUnlock}>
              Enable editing (setup)
            </button>
          </div>
        )}
        {isAccountExecutive && !basic && editUnlocked && (
          <div className="ae-unlocked-banner no-print">
            Editing unlocked (setup mode) — this account is normally view-only.
            <button className="button ae-unlock-button" onClick={toggleEditUnlock}>
              Re-lock
            </button>
          </div>
        )}

        <div className="standard-template-body">
          {/* Forms (ba-template) now open in the SAME tab, so give them a
              consistent "Back" button to return to the previous page — matching
              the Realtor Agent form. Hidden in print. */}
          {className &&
            className.split(" ").includes("ba-template") && (
              <div
                className="ba-back-row no-print"
                style={{ margin: "10px 0" }}
              >
                <button
                  className="button"
                  onClick={handleBackWithSave}
                >
                  ‹ Back
                </button>
              </div>
            )}
          {children}
        </div>
        {!atTop && showScrollToTop && (
          <div className="scroll-to-top" onClick={scrollToTop}>
            <div className="arrow-head" />
          </div>
        )}
      </div>
    </div>
  );
}
